// Angular

// Material
import {
  Component,
  Injector,
  OnInit,
  OnDestroy,
  Input,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

// Third-party
import { Store } from '@ngrx/store';
import { forkJoin, noop, Subject } from 'rxjs';
import { filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { TranslateService } from '@ngx-translate/core';

// Alfresco
import {
  AlfrescoApiService,
  ContentService,
  ContentVersionService,
  PeopleContentService,
  VersionListComponent,
} from '@alfresco/adf-content-services';
import { NotificationService } from '@alfresco/adf-core';
import { PeopleProcessService } from '@alfresco/adf-process-services';
import { NodeEntry, VersionEntry } from '@alfresco/js-api';
import { AppStore, UploadFileVersionAction } from '@alfresco/aca-shared/store';

import {
  CitationExportState,
  downloadCitationLogsDocument,
} from '../../custom-preview/citation-log-export.util';
import { WkVersionService } from '../../shared/services/wk-version.service';
import { RestoreVersionDialogComponent } from '../../../dialogs/save-versions/restore-version.dialog';
import {
  NumberedCitation,
  UnnumberedCitation,
} from '../../../modules/wk-document-decorator/models';
import { CustomContentManagementService } from '../../../services/custom-content-management.service';
import { WkNodePermissionService } from '../../../services/wk-node-permission.service';
import { WkContentApiService } from '../../../services';
import { getAppSelection } from '../../../store/selectors/app.custom-selectors';

interface ExtendedVersionEntry extends VersionEntry {
  canDownloadCitationLogs: boolean;
}

interface VersionMetadataResponse {
  citations?: {
    references?: NumberedCitation[];
    unnumbered_references?: UnnumberedCitation[];
  };
  content?: ArrayBuffer;
}

@Component({
  selector: 'wk-version-list',
  templateUrl: './wk-version-list.component.html',
  styleUrls: ['./wk-version-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'adf-version-list',
  },
  standalone: false,
})
export class WkVersionListComponent
  extends VersionListComponent
  implements OnInit, OnDestroy
{
  @Input() public showDownloadZip = true;
  extendedVersions: any[] = [];
  ondestroy$ = new Subject<void>();
  private readonly customContentService: CustomContentManagementService;
  private readonly translateService: TranslateService;
  private readonly contentApiService: WkContentApiService;
  constructor(
    private readonly dialogRef: MatDialog,
    private readonly injector: Injector,
    private readonly versionService: WkVersionService,
    private readonly ecmUserService: PeopleContentService,
    private readonly peopleProcessService: PeopleProcessService,
    private readonly notificationService: NotificationService,
    private readonly store: Store<AppStore>,
    private readonly wkNodePermissionService: WkNodePermissionService
  ) {
    super(
      injector.get(AlfrescoApiService),
      injector.get(ContentService),
      injector.get(ContentVersionService),
      injector.get(MatDialog)
    );
    this.dialogRef = this.injector.get<MatDialog>(MatDialog);
    this.customContentService =
      this.injector.get<CustomContentManagementService>(
        CustomContentManagementService
      );
    this.translateService =
      this.injector.get<TranslateService>(TranslateService);
    this.contentApiService =
      this.injector.get<WkContentApiService>(WkContentApiService);
  }

  ngOnInit() {
    super.ngOnInit();
    this.loadVersionsHistory();
    this.listenForNodeChanges();
  }

  ngOnDestroy() {
    this.ondestroy$.next();
    this.ondestroy$.complete();
  }

  canUpdate(): boolean {
    if (!this.node) {
      return false;
    }
    return this.wkNodePermissionService.check(this.node, ['update']);
  }

  getUserShortName(user: any) {
    let shortName = '';
    if (user) {
      user = user.split(' ');
      if (user && user.length > 1) {
        shortName = user.shift().charAt(0) + user.pop().charAt(0);
      } else if (user && user.length === 1) {
        shortName = user.shift().charAt(0);
      }
    }
    return shortName.toUpperCase();
  }

  isPictureDefined(user: any): boolean {
    return Boolean(user.pictureId || user.avatarId);
  }

  getUserImage(user: any): string {
    if (user && user.avatarId) {
      return this.ecmUserService.getUserProfileImage(user.avatarId);
    } else {
      return this.peopleProcessService.getUserImage(user);
    }
  }

  onUploadNewVersion() {
    this.store.dispatch(new UploadFileVersionAction({} as any));
  }

  loadVersionsHistory() {
    this.isLoading = true;
    this.versionService.getVersion(this.node.id).subscribe({
      next: (extendedVersions) => {
        this.extendedVersions = extendedVersions.list.entries;
        this.isLoading = false;
      },
      error: () => {
        this.extendedVersions = [];
        this.isLoading = false;
      },
    });
  }

  restoreVersion(version) {
    const dialogRef = this.dialogRef.open(RestoreVersionDialogComponent, {
      data: version,
      maxHeight: '50vh',
      width: '40%',
      panelClass: 'wk-restore-version-dialog',
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((data: any) => data && this.canUpdate()),
        take(1),
        switchMap((data) =>
          this.versionService.revertVersion(this.node.id, version.id, data)
        ),
        take(1),
        switchMap(() => this.versionService.getNodeInformation(this.node.id)),
        takeUntil(this.ondestroy$)
      )
      .subscribe({
        next: (node: NodeEntry) => {
          this.onVersionRestored(node);
          this.openNotificationMessage(version.id);
        },
        error: () => {
          this.notificationService.showError(
            'ADF_VERSION_LIST.RESTORE_VERSION_FAIL'
          );
        },
      });
  }

  onVersionRestored(node: NodeEntry) {
    this.customContentService.versionHistory$.next(true);
    this.restored.emit(node?.entry);
  }

  openNotificationMessage(vId) {
    const messageString = this.translateService.instant(
      'ADF_VERSION_LIST.RESTORE_VERSION_SUCCESS',
      { versionId: vId }
    );
    this.notificationService.openSnackMessage(messageString, 3000);
  }

  public downloadVersionZip(version, versionId: string): void {
    const zip = new JSZip();
    const timestamp = Date.now();

    forkJoin({
      metadata: this.contentApiService.getNodeInfo(this.node.id),
      content: this.versionService.getDocumentByNodeId(this.node.id, versionId),
      contributors: this.versionService.getContributers(this.node.id),
    })
      .pipe(takeUntil(this.ondestroy$))
      .subscribe({
        next: async (data) => {
          let name = version.entry.name;
          const nameParts = name.split('.');
          if (nameParts.length > 1) {
            nameParts.pop();
            name = nameParts.join('.');
          }

          let fileContent = data.content;
          if (fileContent === undefined || fileContent === null) {
            fileContent = '';
          } else if (
            typeof fileContent === 'object' &&
            !(fileContent instanceof Blob) &&
            !(fileContent instanceof ArrayBuffer) &&
            !ArrayBuffer.isView(fileContent)
          ) {
            fileContent =
              typeof fileContent.content === 'string'
                ? fileContent.content
                : JSON.stringify(fileContent);
          }

          zip.file(`${name}.html`, fileContent);
          delete data.content.content;
          zip.file(`${name}.json`, JSON.stringify({ ...data.content }));
          zip.file('node.json', JSON.stringify({ ...data.metadata }));
          zip.file(
            'contributors.json',
            JSON.stringify({ ...data.contributors })
          );
          const content = await zip.generateAsync({ type: 'blob' });
          saveAs(content, `${name}_${versionId}_${timestamp}.zip`);
        },
      });
  }

  public downloadVersionCitationLogs(
    version: ExtendedVersionEntry,
    versionId: string
  ): void {
    this.versionService
      .getDocumentByNodeId(this.node.id, versionId)
      .pipe(takeUntil(this.ondestroy$))
      .subscribe({
        next: (data) => {
          const citationState = this.getCitationExportState(data);

          downloadCitationLogsDocument(
            citationState,
            version?.entry?.name || this.node?.name || 'document',
            version?.entry?.id
          );
        },
        error: () => {
          this.notificationService.showError(
            'APP.ACTIONS.DOWNLOAD_CITATION_LOGS_ERROR'
          );
        },
      });
  }

  private getCitationExportState(
    data: VersionMetadataResponse
  ): CitationExportState {
    const citationsData = data?.citations || {};

    return {
      references: Array.isArray(citationsData?.references)
        ? citationsData.references
        : [],
      unnumbered_references: Array.isArray(citationsData?.unnumbered_references)
        ? citationsData.unnumbered_references
        : [],
    };
  }

  private listenForNodeChanges() {
    this.store
      .select(getAppSelection)
      .pipe(
        filter(
          ({ last }) =>
            !!last?.entry.id &&
            last.entry.isFile &&
            last.entry.id !== this.node.id
        ),
        tap(({ last }) => {
          this.node = last.entry;
          this.loadVersionsHistory();
        }),
        takeUntil(this.ondestroy$)
      )
      .subscribe(noop);
  }
}
